import { useState } from 'react';
import toast from 'react-hot-toast';
import { archivarTramitesMasivo, toggleArchivadoTramite } from '@/services/tramiteService';
import { reportApiError } from '@/common/utils/apiError';
import type { Tramite } from '@/types';

interface ToggleArchivadoParams {
  id: number;
  archivado: boolean;
  motivo?: string;
}

interface ArchivarMasivoParams {
  ids: number[];
  archivado: boolean;
  motivo?: string;
}

export interface ArchivarMasivoResponse {
  message: string;
  archivado: boolean;
  procesados: number;
  omitidos: number;
  ids: number[];
}

/**
 * Archivar/desarchivar escrituras, de a una o en lote.
 *
 * Ambas acciones viven en el mismo hook para que el texto de los toasts y el
 * contexto que se reporta ante un error queden en un solo lugar.
 */
export function useArchivarTramite(onSuccess?: (tramite: Tramite) => void) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toggle = async ({ id, archivado, motivo }: ToggleArchivadoParams): Promise<Tramite> => {
    setIsSubmitting(true);
    try {
      const updated = await toggleArchivadoTramite({ id, archivado, motivo });
      toast.success(archivado ? 'Escritura archivada' : 'Escritura desarchivada');
      onSuccess?.(updated);
      return updated;
    } catch (error) {
      const info = reportApiError(error, {
        operation: 'tramite:toggle-archivado',
        extra: { tramiteId: id, archivado },
      });

      if (!info.isCanceled) {
        toast.error(
          `No se pudo ${archivado ? 'archivar' : 'desarchivar'} la escritura. ${info.message}`,
        );
      }

      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleMasivo = async ({
    ids,
    archivado,
    motivo,
  }: ArchivarMasivoParams): Promise<ArchivarMasivoResponse> => {
    setIsSubmitting(true);
    try {
      const resultado: ArchivarMasivoResponse = await archivarTramitesMasivo({ ids, archivado, motivo });
      toast.success(resultado.message);
      return resultado;
    } catch (error) {
      const info = reportApiError(error, {
        operation: 'tramite:archivar-masivo',
        extra: { cantidad: ids.length, archivado },
      });

      if (!info.isCanceled) {
        // El endpoint es atómico: ante un error no se archivó ninguna, y el
        // mensaje del backend ya explica por qué se rechazó el lote.
        toast.error(
          `No se ${archivado ? 'archivaron' : 'desarchivaron'} las escrituras. ${info.message}`,
        );
      }

      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  return { toggle, toggleMasivo, isSubmitting };
}

export default useArchivarTramite;
